import { detectExif, type ExifResult } from '../../detect/exif';
import { detectFaces, type FaceDetection } from '../../detect/faces';
import { reverseGeocode, type GeoHit } from '../../lib/geocode';
import { ok, err, type Result } from '../../lib/types';
import { recognize, preprocessForOcr, wordsInRange, PSM, type WordBox } from '../../detect/ocr';
import { detectWithRegex } from '../../detect/pii/regex';
import { detectFormFields } from '../../detect/pii/forms';
import { detectNames } from '../../detect/pii/names';
import { mergeDetections } from '../../detect/pii/merge';
import {
  detectLanguage,
  type DetectedLanguage,
} from '../../detect/pii/language';
import type { ImageTextHit, PiiType } from '../../detect/pii/types';

export type DispatchProgressKind = 'ocr-start' | 'ocr-progress' | 'ocr-end';
export type DispatchProgress = {
  kind: DispatchProgressKind;
  /** Free-text message suitable for a toast. */
  message: string;
  /** Optional [0..1] for ocr-progress events. */
  fraction?: number;
};
export type DispatchOpts = {
  onProgress?: (p: DispatchProgress) => void;
};

export type ImageDispatchValue = {
  kind: 'image';
  filename: string;
  outputFilename: string;
  /** Decoded image, ready for canvas operations. */
  bitmap: ImageBitmap;
  /** Original blob (post-HEIC-conversion if applicable). */
  imageBlob: Blob;
  /** Original mime that was decoded — used to pick the output format. */
  mime: string;
  exif: ExifResult;
  geo?: GeoHit;
  faces: FaceDetection[];
  /** True if the input was HEIC and got transcoded. */
  convertedFromHeic: boolean;
  /** OCR-derived PII hits, in normalized [0..1] of the bitmap. */
  imageTextHits: ImageTextHit[];
  /** Raw OCR text — kept around so NER can re-detect after the user
   *  opts into the model (NER doesn't run during dispatch). */
  ocrText?: string;
  /** OCR word boxes parallel to ocrText. Used to lift NER detections
   *  back to image-pixel space when re-detecting. */
  ocrWords?: readonly WordBox[];
  /** Pixel dimensions of the canvas the OCR was run against. Word
   *  bboxes are in this coordinate space, not the original bitmap's,
   *  because we downscale very large photos before OCR to keep memory
   *  bounded. Used by liftToImageTextHits to normalize correctly. */
  ocrCanvasWidth?: number;
  ocrCanvasHeight?: number;
  /** Mean OCR confidence in [0..1]; surfaced as a "review carefully"
   *  warning in the sidebar when below ~0.7. */
  ocrConfidence?: number;
  /** Detected language of the OCR'd text. */
  language?: DetectedLanguage;
};

const HEIC_EXTS = new Set(['heic', 'heif']);
const HEIC_MIMES = new Set(['image/heic', 'image/heif']);

const extensionOf = (name: string): string => {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i + 1).toLowerCase() : '';
};

const isHeic = (file: File): boolean =>
  HEIC_MIMES.has(file.type) || HEIC_EXTS.has(extensionOf(file.name));

const heicToJpeg = async (file: File): Promise<Blob> => {
  const mod = (await import('heic2any')) as unknown as {
    default: (options: { blob: Blob; toType: string; quality?: number }) => Promise<Blob | Blob[]>;
  };
  const out = await mod.default({ blob: file, toType: 'image/jpeg', quality: 0.92 });
  return Array.isArray(out) ? (out[0] ?? new Blob()) : out;
};

const outputFilenameFor = (mime: string): string => {
  if (mime === 'image/jpeg') return 'redacted.jpg';
  if (mime === 'image/webp') return 'redacted.webp';
  return 'redacted.png';
};

const decodeBlob = async (blob: Blob): Promise<ImageBitmap> => {
  return await createImageBitmap(blob);
};

/**
 * Lift OCR text-PII detections into image-pixel rectangles. Each
 * detection becomes one ImageTextHit with one normalized rect per
 * underlying OCR word (multi-word phone numbers etc. produce
 * multiple rects). Empty rect arrays are dropped — they happen when
 * a detection straddles word gaps that OCR didn't expose.
 */
const liftToImageTextHits = (
  words: readonly WordBox[],
  bitmapWidth: number,
  bitmapHeight: number,
  detections: { type: PiiType; text: string; start: number; end: number; confidence: number }[],
): ImageTextHit[] => {
  if (bitmapWidth <= 0 || bitmapHeight <= 0) return [];
  const out: ImageTextHit[] = [];
  for (const d of detections) {
    const overlapping = wordsInRange(words, d.start, d.end);
    if (overlapping.length === 0) continue;
    const rects = overlapping.map((w) => ({
      x: w.bbox.x / bitmapWidth,
      y: w.bbox.y / bitmapHeight,
      w: w.bbox.w / bitmapWidth,
      h: w.bbox.h / bitmapHeight,
    }));
    out.push({
      type: d.type,
      text: d.text,
      rects,
      confidence: d.confidence,
      source: 'ocr',
    });
  }
  return out;
};

/**
 * Dispatch a single image file: optionally transcode HEIC → JPEG, decode it,
 * read EXIF, run face detection, OCR for text PII, and reverse-geocode any
 * GPS coordinates.
 */
export const dispatchImage = async (
  file: File,
  opts: DispatchOpts = {},
): Promise<Result<ImageDispatchValue>> => {
  let imageBlob: Blob = file;
  let convertedFromHeic = false;
  let mime = file.type || 'image/png';

  if (isHeic(file)) {
    try {
      imageBlob = await heicToJpeg(file);
      convertedFromHeic = true;
      mime = 'image/jpeg';
    } catch (cause) {
      return err({
        code: 'HEIC_CONVERT_FAILED',
        message: 'Could not convert this HEIC image. Try exporting as JPEG first.',
        cause,
      });
    }
  } else if (!mime.startsWith('image/')) {
    // Fall back by extension.
    const ext = extensionOf(file.name);
    if (ext === 'png') mime = 'image/png';
    else if (ext === 'jpg' || ext === 'jpeg') mime = 'image/jpeg';
    else if (ext === 'webp') mime = 'image/webp';
  }

  let bitmap: ImageBitmap;
  try {
    bitmap = await decodeBlob(imageBlob);
  } catch (cause) {
    return err({
      code: 'IMAGE_DECODE_FAILED',
      message: "We couldn't decode this image. It may be corrupt or in an unsupported format.",
      cause,
    });
  }

  // Read EXIF from the *original* file (the canvas-encoded blob has none).
  const exif = await detectExif(file);

  let geo: GeoHit | undefined;
  if (exif.gps) {
    geo = reverseGeocode(exif.gps.lat, exif.gps.lng) ?? undefined;
  }

  const faces = await detectFaces(bitmap);

  // OCR runs after face detection so the slow pass is what the user
  // waits on at the end. We render the decoded bitmap into a canvas
  // and pass that to Tesseract — its bundled leptonica decodes
  // PNG/JPEG/BMP natively but doesn't reliably handle WebP, and its
  // `ImageLike` type rejects raw ImageBitmap. Going through a canvas
  // sidesteps both problems for any source format the browser can
  // decode, which is everything we accept.
  opts.onProgress?.({ kind: 'ocr-start', message: `scanning text in ${file.name}…` });
  // Cap OCR input on the longest edge. A 4000×3000 phone photo
  // allocates ~48 MB just for RGBA pixel data; preprocessForOcr then
  // duplicates it. Mobile WebKit OOM-kills the tab. Tesseract's
  // accuracy plateau is around ~2400 px on the longest edge for
  // typical document text — anything beyond that is just memory.
  const MAX_OCR_DIMENSION = 2400;
  const longest = Math.max(bitmap.width, bitmap.height);
  const ocrScale = longest > MAX_OCR_DIMENSION ? MAX_OCR_DIMENSION / longest : 1;
  const ocrW = Math.max(1, Math.round(bitmap.width * ocrScale));
  const ocrH = Math.max(1, Math.round(bitmap.height * ocrScale));
  const ocrCanvas = document.createElement('canvas');
  ocrCanvas.width = ocrW;
  ocrCanvas.height = ocrH;
  const ocrCtx = ocrCanvas.getContext('2d');
  let ocrSource: HTMLCanvasElement | Blob = imageBlob;
  if (ocrCtx) {
    ocrCtx.drawImage(bitmap, 0, 0, ocrW, ocrH);
    preprocessForOcr(ocrCanvas);
    ocrSource = ocrCanvas;
  }
  const ocr = await recognize(
    ocrSource,
    (p) => {
      if (p.status !== 'recognizing text') return;
      opts.onProgress?.({
        kind: 'ocr-progress',
        message: `scanning text… ${String(Math.round(p.progress * 100))}%`,
        fraction: p.progress,
      });
    },
    // Photos of IDs / docs typically have scattered text in different
    // sizes and orientations — sparse-text PSM beats the default
    // auto-segmentation on this kind of layout.
    { pageSegMode: PSM.SPARSE },
  );
  // Free the canvas backing store now that OCR is done.
  ocrCanvas.width = 0;
  ocrCanvas.height = 0;
  opts.onProgress?.({ kind: 'ocr-end', message: '' });

  let imageTextHits: ImageTextHit[] = [];
  let language: DetectedLanguage | undefined;
  if (ocr.text.length >= 4) {
    const [namesDets, lang] = await Promise.all([
      detectNames(ocr.text),
      detectLanguage(ocr.text),
    ]);
    const raw = [
      ...detectWithRegex(ocr.text),
      ...detectFormFields(ocr.text),
      ...namesDets,
    ];
    const merged = mergeDetections(raw);
    imageTextHits = liftToImageTextHits(ocr.words, ocrW, ocrH, merged);
    language = lang;
  }

  const value: ImageDispatchValue = {
    kind: 'image',
    filename: file.name,
    outputFilename: outputFilenameFor(mime),
    bitmap,
    imageBlob,
    mime,
    exif,
    faces,
    convertedFromHeic,
    imageTextHits,
    ...(ocr.text.length > 0 && {
      ocrText: ocr.text,
      ocrWords: ocr.words,
      ocrCanvasWidth: ocrW,
      ocrCanvasHeight: ocrH,
    }),
    ...(ocr.confidence > 0 && { ocrConfidence: ocr.confidence }),
    ...(language !== undefined && { language }),
  };
  if (geo) value.geo = geo;
  return ok(value);
};

export { liftToImageTextHits };
